import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import SEOMeta from "../components/SEOMeta";
import { Reveal, MaskedLine } from "../components/Reveal";
import { useContent } from "../lib/content";
import { api } from "../lib/api";

const CATEGORIES = [
  "General Enquiry",
  "Business Partnership",
  "Business Opportunity",
  "Vendor/Service Provider",
  "Careers",
  "Other",
];

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm px-4 py-3.5 rounded-md outline-none focus:border-gold transition-colors duration-300";

export default function Contact() {
  const content = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: CATEGORIES[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", category: CATEGORIES[0], message: "" });
      toast.success("Your enquiry has been sent. We will get back to you soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please email us directly at support@touson.in.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <SEOMeta
        title="Contact Us | Touson Ventures (OPC) Private Limited"
        description="Contact Touson Ventures for business enquiries, partnerships and collaborations. Based in Baner, Pune, Maharashtra, India. Email: support@touson.in"
      />

      <section className="pt-44 pb-16 sm:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.06),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-gold mb-8"
            data-testid="contact-eyebrow"
          >
            CONTACT US
          </motion.p>
          <h1 className="font-display tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05]" data-testid="contact-headline">
            <MaskedLine delay={0.15}>Let's start a</MaskedLine>
            <MaskedLine delay={0.32} className="gold-gradient-text italic">conversation.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-2xl text-white/65 text-base sm:text-lg leading-relaxed"
            data-testid="contact-intro"
          >
            {content.contact.intro}
          </motion.p>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 space-y-10">
            <Reveal>
              <div data-testid="contact-info-email">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-4">Email</p>
                <a
                  href={`mailto:${content.contact.email}`}
                  data-testid="contact-email-link"
                  className="inline-flex items-center gap-3 font-display text-xl sm:text-2xl text-white hover:text-gold transition-colors duration-300"
                >
                  <Mail size={20} className="text-gold" /> {content.contact.email}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="contact-info-address">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-4">Registered Office</p>
                <div className="flex gap-3">
                  <MapPin size={20} className="text-gold shrink-0 mt-1" />
                  <address className="not-italic text-white/70 text-sm leading-relaxed">
                    <span className="text-white font-medium">Touson Ventures (OPC) Private Limited</span>
                    <br />
                    {content.contact.address_lines.map((line, i) => (
                      <span key={i}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </address>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border border-white/10 overflow-hidden" data-testid="contact-map">
                <iframe
                  title="Touson Ventures Registered Office — Baner, Pune"
                  src="https://maps.google.com/maps?q=Sapphire%20Chambers,%20Baner,%20Pune,%20Maharashtra%20411045&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-64 border-0"
                  style={{ filter: "grayscale(1) invert(0.92) contrast(0.9)" }}
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 sm:p-12 space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2.5">
                      Name *
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2.5">
                      Email *
                    </label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@company.com"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-phone" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2.5">
                      Phone
                    </label>
                    <input
                      id="contact-phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-category" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2.5">
                      Enquiry Type *
                    </label>
                    <select
                      id="contact-category"
                      data-testid="contact-category-select"
                      required
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className={`${inputCls} appearance-none cursor-pointer`}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c} className="bg-navy-800 text-white">
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2.5">
                    Message *
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your enquiry, partnership idea or opportunity..."
                    className={`${inputCls} resize-none`}
                  />
                </div>
                {sent && (
                  <p className="text-sm text-gold" data-testid="contact-success-message">
                    Thank you — your enquiry has been received. We typically respond within a few business days.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 bg-gold text-navy font-medium text-sm px-10 py-4 rounded-full hover:bg-gold-light transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending..." : "Send Enquiry"}
                  <Send size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
